import {
  type InteractionSource,
  type SelectionRange,
  type SelectionState,
} from '../../../state/types';
import { type ReducerResponse } from '../../types';

type RemoveSingleItemRangeParams = {
  state: SelectionState;
  range: SelectionRange;
  source: InteractionSource;
  mode: SelectionState['mode'];
};

/**
 * Removes a single-item range from selections.
 * @example
 * // User has rows 2 and 5 selected, clicks row 2 to deselect it
 * // Before: selections = [
 * //   { id: 'r1', anchor: { row: 2, col: 0 }, lead: { row: 2, col: 0 } },
 * //   { id: 'r2', anchor: { row: 5, col: 0 }, lead: { row: 5, col: 0 } }
 * // ]
 * // After: selections = [{ id: 'r2', anchor: { row: 5, col: 0 }, lead: { row: 5, col: 0 } }]
 *
 * @returns A reducer response with updated state and delta
 */
export function removeSingleItemRange({
  state,
  range,
  source,
  mode,
}: RemoveSingleItemRangeParams): ReducerResponse {
  const updatedSelections = state.selections.filter((r) => r.id !== range.id);

  return {
    delta: {
      kind: 'selectionModified',
      source,
      rangeIdsToRemove: [range.id],
      focusChange: {
        prev: state.focusedCell,
        next: null,
      },
    },
    next: {
      ...state,
      mode: updatedSelections.length === 0 ? 'idle' : mode,
      selections: updatedSelections,
      focusedCell: null,
      lastInteractionSource: source,
    },
  };
}
